/**
 * ContextHero - the S-DASH context bar (UX-RW-03 v1.1 S-DASH): the selected
 * TPIN verbatim, its branch count, Change TPIN, the period selector and the
 * source freshness with the explicit Refresh action.
 *
 * Presentational: props in, events out. Refresh is a button the user presses;
 * nothing here schedules a re-read (PD-33). The freshness is the source's
 * timestamp as delivered, or an honest absence - never the page-load time.
 * Long values wrap (min-width:0 + overflow-wrap) so the TPIN, the freshness
 * and the buttons can never overlap at 390px.
 */

import type { PeriodSelection } from '@domain/types'
import type { FreshnessState } from '@domain/tpin'
import { NO_TIMESTAMP_TEXT, branchCountWord } from '@domain/tpinDash'
import { tpinPath } from '@domain/tpinPaths'
import { formatFreshness } from './FreshnessIndicator'
import { Link } from './Link'
import { PeriodSelector } from './PeriodSelector'
import { REFRESHING_LABEL, REFRESH_TPIN_LABEL, REFRESH_TPIN_NOTE } from './refreshCopy'

function freshnessLabel(freshness: FreshnessState): string {
  if (freshness.kind === 'pending') return 'Loading…'
  if (freshness.kind === 'absent') return NO_TIMESTAMP_TEXT
  return formatFreshness(freshness.raw)
}

export function ContextHero({
  tin,
  branchesTotal,
  period,
  freshness,
  refreshing,
  onRefresh,
}: {
  readonly tin: string
  /** The server's branches_total; null until the summary is complete. */
  readonly branchesTotal: number | null
  readonly period: PeriodSelection
  readonly freshness: FreshnessState
  readonly refreshing: boolean
  readonly onRefresh: () => void
}) {
  return (
    <section className="dash-hero" aria-label="Customer context" data-testid="dash-hero">
      <div className="dash-hero-row">
        <div className="dash-hero-id">
          <h1 className="dash-hero-tin" data-testid="tpin-title">{`TPIN ${tin}`}</h1>
          {branchesTotal !== null && (
            <p className="dash-hero-branches" data-testid="dash-branch-count">
              {branchCountWord(branchesTotal)}
            </p>
          )}
        </div>
        <Link to="/" period={period} className="dash-hero-change" testid="change-tpin">
          Change TPIN
        </Link>
      </div>
      <div className="dash-hero-row">
        <div className="dash-freshness" aria-live="polite" data-testid="dash-freshness">
          <span className="dash-freshness-label">Source data as of:</span>
          <span className="dash-freshness-value" data-testid="dash-freshness-value">
            {freshnessLabel(freshness)}
          </span>
          <span className="dash-freshness-note" data-testid="refresh-note">
            {REFRESH_TPIN_NOTE}
          </span>
        </div>
        <button
          type="button"
          className="dash-hero-refresh"
          onClick={onRefresh}
          disabled={refreshing}
          aria-busy={refreshing || undefined}
          data-testid="refresh-button"
        >
          {refreshing ? REFRESHING_LABEL : REFRESH_TPIN_LABEL}
        </button>
      </div>
      <div className="dash-hero-period">
        <PeriodSelector period={period} pathname={tpinPath(tin)} />
      </div>
    </section>
  )
}
